import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, ShieldCheck, X, FileText, Loader2, AlertTriangle, BadgePercent } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WizardSteps from "@/components/WizardSteps";
import AdPreview from "@/components/AdPreview";
import { useBooking } from "@/context/BookingContext";
import { api, formatApiError } from "@/lib/api";

const GST_PERCENT = 5;
const DISCOUNT_PERCENT = 5;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function CheckoutStep() {
  const nav = useNavigate();
  const { draft, update, reset } = useBooking();
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [papers, setPapers] = useState([]);

  React.useEffect(() => {
    api.get("/papers").then(({ data }) => setPapers(data)).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!draft.paper_id) nav("/book/compose", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paper = useMemo(() => papers.find((p) => p.id === draft.paper_id), [papers, draft.paper_id]);

  const flatRate = paper
    ? Number(draft.ad_type === "change_of_name" ? (paper.change_of_name_rate || 0) : paper.flat_rate)
    : 0;
  const discountAmount = +(flatRate * DISCOUNT_PERCENT / 100).toFixed(2);
  const discountedBase = +(flatRate - discountAmount).toFixed(2);
  const gstAmount = +(discountedBase * GST_PERCENT / 100).toFixed(2);
  const adMakingFee = draft.ad_mode === "make_my_ad" ? 118 : 0;
  const total = +(discountedBase + gstAmount + adMakingFee).toFixed(2);

  const onLetterhead = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { setErr("Letterhead must be under 4 MB"); return; }
    const url = await fileToDataUrl(f);
    update({ letterhead_data_url: url, letterhead_name: f.name });
  };

  const placeOrderAndPay = async () => {
    setErr("");
    if (!agreed) { setErr("Please confirm you have read & agreed to the disclaimer before proceeding."); return; }
    // Letterhead is only required for Public Notice bookings
    if (draft.ad_type !== "change_of_name" && !draft.letterhead_data_url) {
      setErr("Please upload the advocate letterhead (with stamp & signature)."); return;
    }
    if (draft.ad_type === "change_of_name" && !draft.affidavit_data_url) {
      setErr("Please upload the Affidavit copy on the previous step."); return;
    }
    setBusy(true);
    try {
      const payload = {
        ad_type: draft.ad_type || "public_notice",
        paper_id: draft.paper_id,
        edition: draft.edition || "sambhajinagar",
        publish_date: draft.publish_date,
        language: draft.language || "english",
        ad_size: draft.ad_size || "4x10",
        ad_mode: draft.ad_mode,
        ad_text: draft.ad_text || null,
        ad_file_name: draft.ad_file_name || null,
        ad_file_data_url: draft.ad_file_data_url || null,
        letterhead_data_url: draft.letterhead_data_url || null,
        advocate: draft.advocate,
        // Change-of-Name specific
        con_subtype: draft.con_subtype || "name_only",
        relation: draft.relation || "self",
        army_no: draft.army_no || null,
        rank_label: draft.rank_label || null,
        old_name: draft.old_name || null,
        new_name: draft.new_name || null,
        old_dob: draft.old_dob || null,
        new_dob: draft.new_dob || null,
        affidavit_number: draft.affidavit_number || null,
        affidavit_date: draft.affidavit_date || null,
        affidavit_file_name: draft.affidavit_file_name || null,
        affidavit_data_url: draft.affidavit_data_url || null,
      };
      const { data: booking } = await api.post("/bookings", payload);
      const { data: order } = await api.post("/payment/create-order", { booking_id: booking.id });

      if (order.mock || !order.key_id) {
        await api.post("/payment/verify", {
          booking_id: booking.id,
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `pay_mock_${Math.random().toString(36).slice(2,12)}`,
          razorpay_signature: "mock",
        });
        reset();
        nav(`/book/done?b=${booking.id}&mock=1`);
        return;
      }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Welworth Creative Media",
        description: `Public Notice in ${paper?.name || "newspaper"}`,
        order_id: order.order_id,
        prefill: { name: draft.advocate.name, contact: draft.advocate.contact_primary },
        theme: { color: "#0A0A0A" },
        handler: async (rp) => {
          try {
            await api.post("/payment/verify", {
              booking_id: booking.id,
              razorpay_order_id: rp.razorpay_order_id,
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_signature: rp.razorpay_signature,
            });
            reset();
            nav(`/book/done?b=${booking.id}`);
          } catch (e) { setErr(formatApiError(e)); }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      if (!window.Razorpay) { setErr("Razorpay SDK not loaded"); setBusy(false); return; }
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 w-full">
        <WizardSteps current="checkout" />
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <div>
              <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Step 03</div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-black mt-2 font-bold tracking-tight">Letterhead &amp; payment.</h1>
            </div>

            {err && <div className="px-4 py-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-testid="checkout-error">{err}</div>}

            {/* AD PREVIEW */}
            <div className="bg-white border border-black/15 rounded-sm p-5 sm:p-7 space-y-4">
              <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Preview</div>
              <AdPreview
                adType={draft.ad_type || "public_notice"}
                conSubtype={draft.con_subtype || "name_only"}
                relation={draft.relation || "self"}
                armyNo={draft.army_no}
                rankLabel={draft.rank_label}
                language={draft.language || "english"}
                body={draft.ad_mode === "text" ? draft.ad_text : draft.ad_mode === "make_my_ad" ? "" : `[Uploaded file: ${draft.ad_file_name || "your ad"}]`}
                advocate={draft.advocate}
                paperName={paper?.name}
                publishDate={draft.publish_date}
                oldName={draft.old_name}
                newName={draft.new_name}
                oldDob={draft.old_dob}
                newDob={draft.new_dob}
                affidavitNumber={draft.affidavit_number}
                affidavitDate={draft.affidavit_date}
              />
            </div>

            {/* Letterhead Upload — only for Public Notice */}
            {draft.ad_type !== "change_of_name" && (
            <div className="bg-white border border-black/15 rounded-sm p-5 sm:p-7 space-y-4">
              <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Advocate Letterhead</div>
              <p className="text-sm text-gray-600">Upload a scanned letterhead bearing your authorisation for this notice (PDF / JPG / PNG, max 4 MB).</p>
              <div className="p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-900 text-sm" data-testid="letterhead-stamp-note">
                <strong>Advocate&apos;s stamp and signature is mandatory for publishing Ad.</strong>
              </div>
              {draft.letterhead_data_url ? (
                <div className="flex items-center justify-between px-4 py-3 border border-black/15 rounded-sm bg-gray-50" data-testid="letterhead-preview">
                  <div className="flex items-center gap-3 text-sm">
                    <FileText size={18} className="text-black"/>
                    <span className="font-mono break-all">{draft.letterhead_name || "letterhead.pdf"}</span>
                  </div>
                  <button onClick={() => update({ letterhead_data_url: "", letterhead_name: "" })} className="text-gray-400 hover:text-red-500 shrink-0" data-testid="letterhead-remove">
                    <X size={16}/>
                  </button>
                </div>
              ) : (
                <label className="block">
                  <input type="file" accept="application/pdf,image/png,image/jpeg" capture="environment" className="hidden" onChange={onLetterhead} data-testid="letterhead-input"/>
                  <div className="border border-dashed border-black/25 rounded-sm p-6 sm:p-8 text-center hover:border-black transition cursor-pointer bg-gray-50">
                    <Upload size={28} className="mx-auto text-black"/>
                    <div className="font-display text-lg sm:text-xl text-black mt-3 font-bold">Tap to upload letterhead</div>
                    <div className="text-xs text-gray-500 mt-1">PDF, PNG, or JPG · max 4 MB</div>
                  </div>
                </label>
              )}
            </div>
            )}

            {/* Change of Name — verification disclaimer at checkout */}
            {draft.ad_type === "change_of_name" && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-sm" data-testid="checkout-con-disclaimer">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-yellow-700 mt-0.5 shrink-0"/>
                  <div className="flex-1">
                    <div className="text-[10px] tracking-overline uppercase text-yellow-800 font-bold">Change of Name · Publication Notice</div>
                    <div className="text-sm text-yellow-900 mt-1"><strong>This ad will be published subject to verification.</strong> Our team will verify the Affidavit copy (Affidavit No. <span className="font-mono">{draft.affidavit_number || "—"}</span>) before publication. Fraudulent submissions will be reported to the concerned newspaper and law enforcement.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-sm" data-testid="checkout-disclaimer">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-amber-700 mt-0.5 shrink-0"/>
                <div className="flex-1">
                  <div className="text-[10px] tracking-overline uppercase text-amber-700 font-bold">Disclaimer</div>
                  <p className="text-sm text-amber-900 mt-1 leading-relaxed">
                    <strong>Publication of the Ad is subject to the approval of the Newspaper.</strong> In the rare event a notice is rejected or held back by the newspaper&apos;s legal desk, we will refund the amount or re-route to an alternate publication of your choice.
                  </p>
                  <label className="mt-3 flex items-start gap-2 text-sm text-amber-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-black"
                      data-testid="disclaimer-agree"
                    />
                    <span>I have read and agree to the above disclaimer.</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Review block */}
            <div className="bg-white border border-black/15 rounded-sm p-5 sm:p-7 space-y-4">
              <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Review</div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Row k="Newspaper" v={paper?.name || "—"} />
                <Row k="Publish date" v={draft.publish_date || "—"} />
                <Row k="Language" v={draft.language ? draft.language.charAt(0).toUpperCase() + draft.language.slice(1) : "—"} />
                <Row k="Ad size" v={draft.ad_size || "—"} />
                <Row k="Ad mode" v={draft.ad_mode === "text" ? "Type / paste text" : draft.ad_mode === "upload" ? `Upload (${draft.ad_file_name})` : "Make my Ad (₹118)"} />
                <Row k="Advocate" v={draft.advocate.name || "—"} />
              </div>
            </div>
          </div>

          {/* Right - Invoice */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 bg-white border border-black/15 rounded-sm p-5 sm:p-6 space-y-4" data-testid="invoice-summary">
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Invoice Summary</div>
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-[10px] tracking-overline uppercase font-bold rounded-sm border border-emerald-200" data-testid="discount-badge">
                  <BadgePercent size={12}/> 5% off online
                </div>
              </div>
              <div className="text-sm space-y-2.5 font-mono">
                <Line k={`${paper?.name || "Newspaper"} · flat rate`} v={`₹${flatRate.toFixed(2)}`} />
                <Line k={`Online booking discount (${DISCOUNT_PERCENT}%)`} v={`– ₹${discountAmount.toFixed(2)}`} className="text-emerald-700" testid="line-discount"/>
                <Line k="Discounted base" v={`₹${discountedBase.toFixed(2)}`} testid="line-discounted-base"/>
                <Line k={`GST @ ${GST_PERCENT}% (on discounted base)`} v={`₹${gstAmount.toFixed(2)}`} testid="line-gst"/>
                {adMakingFee > 0 && (
                  <div>
                    <Line k="Make-my-Ad service" v={`₹${adMakingFee.toFixed(2)}`} testid="line-ad-making"/>
                    <div className="text-[10px] text-gray-500 italic ml-0 mt-0.5">incl. 18% GST on ad-making</div>
                  </div>
                )}
                <div className="border-t border-black/15 pt-3 flex justify-between text-lg font-bold text-black">
                  <span>Total payable</span><span data-testid="checkout-total">₹{total.toFixed(2)}</span>
                </div>
              </div>
              <button
                onClick={placeOrderAndPay}
                disabled={busy}
                className="w-full bg-black text-white py-3.5 rounded-sm hover:bg-gray-800 transition font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="checkout-pay-btn"
              >
                {busy && <Loader2 size={16} className="animate-spin"/>}
                {busy ? "Processing…" : `Pay ₹${total.toFixed(2)} via Razorpay`}
              </button>
              <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
                <ShieldCheck size={12}/> Secured by Razorpay · GST invoice emailed
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed text-center">
                Rates apply to <strong>4 × 10</strong> Public Notice ads. Publication subject to newspaper approval.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div>
      <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">{k}</div>
      <div className="text-black font-medium break-words">{v}</div>
    </div>
  );
}

function Line({ k, v, className = "", testid }) {
  return (
    <div className={`flex justify-between gap-3 ${className}`} data-testid={testid}>
      <span className="text-gray-600">{k}</span>
      <span>{v}</span>
    </div>
  );
}
